import React, { useMemo } from "react";
//#region redux
import { useSelector } from "react-redux";
//#endregion
//#region types
import { DimensionData, DimensionTypeItemMultiple } from "models/propTypes/dimensionPropTypes";
import { DimensionEnums } from "models/enums";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { DimensionSelectorExtraProps, FormFieldProps } from "models/propTypes/genericFormProps";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
//#endregion
//#region components
import useMountedState from "../../useMountedState";
import ReadOnlyField from "../baseFields/readOnlyField";
import { DimensionSelector } from "../baseFields/dimensionSelector";
import { Grid } from "@mui/material";
import { useDimensionService } from "serviceHooks";
//#endregion
import { DimensionHelper, LocalizationHelperInstance, MathHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

function DimensionSelectorFormField<T extends Record<string, any>>(props: FormFieldProps<T>) {
  const dimensionService = useDimensionService();
  const { name, field, value, readonly, disabled } = props;
  const { getMounted } = useMountedState();
  const [dimensionMap, setDimensionMap] = React.useState<DimensionTypeItemMultiple<AutocompleteListItem>[]>([]);

  const {
    type,
    amount,
    currency,
    alwaysDisplayAmounts,
    allowDefaultDimensions,
    valueFormatter,
    onViewAllClick,
    viewAllButtonText,
    enableSingleSelectionMode,
    hideSelectionDialogFooter,
    allowedTypes,
    hideViewAll,
    fileViewer,
  } = field.extraProps as DimensionSelectorExtraProps;

  const dimensionPresetType = useMemo(() => {
    switch (type) {
      case DimensionEnums.DimensionCategory.IncomingInvoice:
        return WintSuperkollAPIModel.DimensionPresetType.IncomingInvoice;
      case DimensionEnums.DimensionCategory.Invoice:
        return WintSuperkollAPIModel.DimensionPresetType.Invoice;
      case DimensionEnums.DimensionCategory.Receipt:
        return WintSuperkollAPIModel.DimensionPresetType.Receipt;
      default:
        return undefined;
    }
  }, [type]);

  const { dimensionTypes, dimensionSuggestions, dimensions } = useSelector(
    DimensionHelper.getAppStateDimensionSelector(type)
  );

  React.useEffect(() => {
    getMounted() && setDimensionMap(getDimensionTypeMap());
  }, [dimensionSuggestions, dimensions, value]);

  React.useEffect(() => {
    dimensionMap.length > 0 && onAmountUpdate();
  }, [amount, currency]);

  const getDimensionTypeMap = (): DimensionTypeItemMultiple<AutocompleteListItem>[] => {
    let selectedUpdated = false;
    const selectedDimensions: WintSuperkollAPIModel.DimensionData[] = value
      ? valueFormatter
        ? (valueFormatter(value) ?? [])
        : value
      : [];
    const selectedDimensionIds = selectedDimensions.map((dim) => dim.dimensionId);

    const allowedDimensionTypes: WintSuperkollAPIModel.DimensionType[] = allowedTypes?.length
      ? dimensionTypes.filter((d) => allowedTypes.includes(d.type))
      : dimensionTypes;

    const dimensionsMap = allowedDimensionTypes.map((companyDimType) => {
      const all = dimensions.find((d) => d.key.id === companyDimType.id);
      const suggestions = dimensionSuggestions.find((ds) => ds.key.id === companyDimType.id)?.value ?? [];
      const type = all?.key ?? companyDimType;

      const currentDimensionValues = dimensionMap.find((d) => d.id === companyDimType.id)?.selectedDimensions;

      let selected = all
        ? all.value
            .filter((item) => selectedDimensionIds.includes(item.id as string))
            .map((item) => {
              const selectedDim = selectedDimensions.find((d) => d.dimensionId === item.id);
              const currentData = currentDimensionValues?.find((d) => d.id === item.id)?.data;

              return dimensionService.convertDimensionToSelectionItem(
                item,
                amount,
                currency,
                selectedDim,
                alwaysDisplayAmounts,
                currentData
              );
            })
        : [];

      if (
        (!dimensionMap || dimensionMap.length === 0) &&
        allowDefaultDimensions &&
        selectedDimensions.length === 0 &&
        !!type.recommendedDimensionId
      ) {
        const item = all?.value.find((item) => item.id === type.recommendedDimensionId);
        const selectedDim = {
          dimensionId: type.recommendedDimensionId ?? "",
          amount: amount,
        };

        if (item) {
          selectedUpdated = true;
          selected = [
            dimensionService.convertDimensionToSelectionItem(item, amount, currency, selectedDim, alwaysDisplayAmounts),
          ];
        }
      }

      return {
        id: type.id ?? "",
        name: type.name ?? "",
        type: type.type,
        suggestions: dimensionService.convertDimensionToSelectionItems(suggestions, amount, currency),
        selectedDimensions: selected,
        all: all ? dimensionService.convertDimensionToSelectionItems(all.value, amount, currency) : [],
      };
    });

    if (selectedUpdated) raiseOnChangeEvent(dimensionsMap);
    return dimensionsMap;
  };

  const evenSplitItems = (items: AutocompleteListItem[]) => {
    const itemsCount = items.length;
    if (itemsCount) {
      const percentage = MathHelper.toFixedNoRound(100 / itemsCount, 2);
      const itemAmount = MathHelper.toFixedNoRound(amount / itemsCount, 2);

      return items.map((i, index) => ({
        ...i,
        data: {
          ...i.data,
          amount: itemAmount,
          percentage: percentage,
          fraction: [1, itemsCount],
          isValid: true,
        },
      }));
    } else {
      return items;
    }
  };

  const processSelectionItem = (item: AutocompleteListItem) => {
    const itemData =
      item.data && Object.keys(item.data).length !== 0
        ? (item.data as DimensionData)
        : ({
            dimensionId: item.id,
            amount: amount,
            percentage: 100,
            fraction: [1, 1],
            isValid: true,
          } as DimensionData);

    return {
      ...item,
      data: itemData,
      selectedSubText:
        itemData.percentage === 100
          ? ""
          : LocalizationHelperInstance.getFormattedCurrency(itemData.amount, currency, 2, 2),
    };
  };

  const onSelectionChange = (
    typeId: string,
    evenlySplit: boolean,
    append: boolean,
    selectedItems: AutocompleteListItem[]
  ) => {
    const dimensionsNew = [...dimensionMap];
    const dim = dimensionsNew.find((x) => x.id === typeId);
    const selectedItemsModified =
      evenlySplit && !enableSingleSelectionMode
        ? evenSplitItems(
            append
              ? [
                  ...(dim?.selectedDimensions ?? []),
                  ...selectedItems.filter(
                    (s) => !dim?.selectedDimensions.some((selectedDim) => s.id === selectedDim.id)
                  ),
                ]
              : selectedItems
          )
        : selectedItems;
    const processedSelectedItems = selectedItemsModified.map((item) => processSelectionItem(item));
    if (dim) dim.selectedDimensions = processedSelectedItems;

    setDimensionMap(dimensionsNew);
    raiseOnChangeEvent(dimensionsNew);
  };

  const raiseOnChangeEvent = (dimensionsUpdated: DimensionTypeItemMultiple<AutocompleteListItem>[]) => {
    props.handleOnChangeCustom(
      name,
      dimensionsUpdated.flatMap((dim) =>
        dim.selectedDimensions.map((item) => ({
          dimensionId: item.id as string,
          amount: (item.data as DimensionData).amount,
        }))
      )
    );
    props.handleOnBlurCustom(name);
  };

  const onAmountUpdate = () => {
    const dimensionsNew = [...dimensionMap];
    dimensionsNew.forEach((d) => {
      d.selectedDimensions.forEach((sd) => {
        const data = sd.data as DimensionData;
        const newAmount = amount ? (amount * data.fraction[0]) / data.fraction[1] : 0;
        sd.data = {
          ...sd.data,
          amount: newAmount,
        };

        sd.selectedSubText =
          data.percentage === 100 ? "" : LocalizationHelperInstance.getFormattedCurrency(newAmount, currency, 2, 2);
      });
    });
    setDimensionMap(dimensionsNew);
    raiseOnChangeEvent(dimensionsNew);
  };

  const getReadOnlySelectionGroup = (dimensionType: DimensionTypeItemMultiple<AutocompleteListItem>, index: number) => (
    <ReadOnlyField
      key={index}
      label={DimensionHelper.getDimensionTypeText(dimensionType.type, dimensionType.name)}
      href={field.href}
      className={cssExports.readOnlyTextField}
    >
      {getDimensionSelector(dimensionType)}
    </ReadOnlyField>
  );

  const getDimensionSelector = (dimensionType: DimensionTypeItemMultiple<AutocompleteListItem>) => (
    <DimensionSelector
      dimensionType={dimensionType}
      onSelectionChange={onSelectionChange}
      onViewAllClick={onViewAllClick}
      viewAllButtonText={viewAllButtonText}
      enableSingleSelectionMode={enableSingleSelectionMode}
      hideSelectionDialogFooter={hideSelectionDialogFooter}
      dimensionPresetType={dimensionPresetType}
      amount={amount}
      currency={currency}
      disabled={disabled}
      readonly={readonly}
      hideViewAll={hideViewAll}
      fileViewer={fileViewer}
    />
  );

  return (
    <>
      {readonly ? (
        <Grid container item spacing={2}>
          {dimensionMap.map((dimensionType, index) => {
            return dimensionType.selectedDimensions.length > 0 ? (
              props.layout ? (
                <props.layout.type key={index} {...props.layout.props}>
                  {getReadOnlySelectionGroup(dimensionType, index)}
                </props.layout.type>
              ) : (
                getReadOnlySelectionGroup(dimensionType, index)
              )
            ) : (
              <React.Fragment key={index}></React.Fragment>
            );
          })}
        </Grid>
      ) : (
        dimensionMap.map((dimensionType, index) => {
          return props.layout ? (
            <props.layout.type
              key={dimensionType.id}
              {...props.layout.props}
              label={DimensionHelper.getDimensionTypeText(dimensionType.type, dimensionType.name)}
              enableControlLables
            >
              {getDimensionSelector(dimensionType)}
            </props.layout.type>
          ) : (
            getDimensionSelector(dimensionType)
          );
        })
      )}
    </>
  );
}

export default DimensionSelectorFormField;
